import {defineMessages, FormattedMessage} from 'react-intl';

import {push} from 'connected-react-router';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {HOTLINE} from '@constants/subscriptions';

import {
  DEFAULT_CLIENT_URL_STRING_KEY,
  DEFAULT_HOTLINE_KEY,
  DEFAULT_PARENT_ID,
  DEFAULT_PARENT_KEY,
} from '../../../components/Organizations/constants';
import {ORG_ROLE_TAB_PATHS, ORG_TAB_PATHS, ROUTE_URLS} from '../../../constants/routes';
import {createSortParams} from '../../../services/api/wa-request';
import {notificationActions} from '../notification';
import {parseSolutions} from '../roleManagement/utils';
import {sessionActions} from '../session';
import * as types from './types';
import {
  assignPermissionsToOrgSolutions,
  formatRolePayload,
  parseApiResult,
  processOrganizationHierarchy,
  processParentOrganizationHierarchy,
} from './utils';

const messages = defineMessages({
  urlAndHotlineUpdateSuccess: {
    id: 'Redux.organization.urlAndHotlineUpdateSuccess',
    defaultMessage: 'URL / Hotline configurations successfully saved.',
  },
  urlAndHotlineUpdateFailure: {
    id: 'Redux.organization.urlAndHotlineUpdateFailure',
    defaultMessage: 'URL / Hotline configurations could not be saved.',
  },
  urlUpdateSuccess: {
    id: 'Redux.organization.urlUpdateSuccess',
    defaultMessage: 'Configuration URL Updated',
  },
  urlUpdateFailure: {
    id: 'Redux.organization.urlUpdateFailure',
    defaultMessage: 'Configuration URL Failed to Update',
  },
  hotlineUpdateSuccess: {
    id: 'Redux.organization.hotlineUpdateSuccess',
    defaultMessage: 'Configuration hotline number Updated',
  },
  hotlineUpdateFailure: {
    id: 'Redux.organization.hotlineUpdateFailure',
    defaultMessage: 'Configuration hotline failed to update. Please try a different hotline number.',
  },
  organizationSubscriptionsUpdatedSuccess: {
    id: 'Redux.organization.organizationSubscriptionsUpdatedSuccess',
    defaultMessage: 'Subscriptions have been updated.',
  },
  organizationSubscriptionsUpdateError: {
    id: 'Redux.organization.organizationSubscriptionsUpdateError',
    defaultMessage: 'Failed to update subscriptions',
  },
  organizationMaxSeatsUpdatedSuccess: {
    id: 'Redux.organization.organizationMaxSeatsUpdatedSuccess',
    defaultMessage: 'Max seat count has been updated.',
  },
  organizationCustomRoleCreatedTitle: {
    id: 'Redux.organization.organizationCustomRoleCreatedTitle',
    defaultMessage: 'Custom Role Created',
  },
  organizationCustomRoleUpdatedTitle: {
    id: 'Redux.organization.organizationCustomRoleUpdatedTitle',
    defaultMessage: 'Custom Role Updated',
  },
  organizationCustomRoleDeletedTitle: {
    id: 'Redux.organization.organizationCustomRoleDeletedTitle',
    defaultMessage: 'Role Deleted',
  },
  organizationCustomRoleDeletedFail: {
    id: 'Redux.organization.organizationCustomRoleDeletedFail',
    defaultMessage: 'Role could not be deleted',
  },
  organizationCustomRoleCreatedDescription: {
    id: 'Redux.organization.organizationCustomRoleCreatedDescription',
    defaultMessage: 'Custom role can now be assigned to users in your organization',
  },
  bulkAssignSuccess: {
    id: 'Redux.organization.bulkAssignSuccess',
    defaultMessage: 'Role Bulk Assigned',
  },
  bulkAssignSuccessDescription: {
    id: 'Redux.organization.bulkAssignSuccessDescription',
    defaultMessage: 'All users within {orgName} and its child organization are now assigned to {roleName}',
  },
  bulkAssignFailure: {
    id: 'Redux.organization.bulkAssignFailure',
    defaultMessage: 'Failed to Bulk Assign the role',
  },
  bulkReassignSuccess: {
    id: 'Redux.organization.bulkReassignSuccess',
    defaultMessage: 'Role Bulk Reassigned',
  },
  bulkReassignSuccessDescription: {
    id: 'Redux.organization.bulkReassignSuccessDescription',
    defaultMessage:
      '{numberOfUsersReAssigned} users have been unassigned from {roleNameToRemove} and are now assigned to the {roleNameToAdd} role',
  },
  bulkReassignFailure: {
    id: 'Redux.organization.bulkReassignFailure',
    defaultMessage: 'Failed to Bulk Reassign the role',
  },
});

/**
 * Dispatches a request action only if the current store
 * lorganization.tree is empty.
 *
 * @returns {Function}
 */
export const requestOrganizationTreeIfNecessary = () => {
  return (dispatch, getState) => {
    const currentOrgTree = get(getState(), 'organization.tree');
    const needToRequest = isEmpty(currentOrgTree);

    if (needToRequest) {
      return dispatch(request());
    }
  };
};

export const request = (page, size, sortField, sortDir, searchFilter) => {
  return (dispatch, getState, {organization}) => {
    // TODO remove this defaulting
    const state = (getState && getState()) || {};
    const organizationId = get(state, 'session.organizationId', DEFAULT_PARENT_ID);

    const sort = createSortParams(sortField, sortDir);

    dispatch({type: types.CHILDREN_REQUEST});

    return organization
      .getOrganizationChildren(organizationId, page, size, sort, searchFilter)
      .then((results) => {
        const payload = {
          ...parseApiResult(results),
          ...{sortField: sortField, direction: sortDir},
        };
        return dispatch({
          type: types.CHILDREN_SUCCESS,
          payload: payload,
        });
      })
      .catch((error) =>
        dispatch({
          type: types.CHILDREN_FAILURE,
          error,
        }),
      );
  };
};

/**
 * Get the entire hierarchy of a given Organization. This includes the
 * Organization's details as well as any children of the Organization.
 *
 * If an Organization matching the id does not exist, 404 will be returned.
 * If the Organization matching the id does not have any children only the Organization requested will be returned.
 * @param {uuid} organizationId Mandatory - the ID of the (parent) organization
 * @returns {function(*, *, {organization: *}): Promise<unknown>}
 */
export const getOrganizationHierarchy = (organizationId) => {
  return (dispatch, _getState, {organization}) => {
    dispatch({type: types.HIERARCHY_REQUEST});

    return organization
      .getOrganizationHierarchy(organizationId)
      .then((results) => {
        processOrganizationHierarchy(results.data);

        return dispatch({
          type: types.HIERARCHY_SUCCESS,
          payload: results,
        });
      })
      .catch((error) =>
        dispatch({
          type: types.HIERARCHY_FAILURE,
          error,
        }),
      );
  };
};

/**
 * Given a node in an organization hierarchy, returns the entire hierarchy of the organization MINUS that given node and all of its children.
 *
 * If you specify the id of a node to "rootNodeId", you will get the entire organization between the node with that id and the given node
 * (minus all the children of the given node)
 * @param node {object} a node in the organization hierarchy
 * @param rootNodeId {string} optional. Specify a different parent rather than starting from the Root organization.
 * @returns {function(*, *, {organization: *}): Promise<unknown>}
 */
export function getParentOrganizationHierarchy(node, rootNodeId = DEFAULT_PARENT_ID) {
  return (dispatch, _getState, {organization}) => {
    dispatch({type: types.PARENT_HIERARCHY_REQUEST});

    return organization
      .getOrganizationHierarchy(rootNodeId)
      .then((results) => {
        processParentOrganizationHierarchy(results.data, node);

        return dispatch({
          type: types.PARENT_HIERARCHY_SUCCESS,
          payload: results,
        });
      })
      .catch((error) =>
        dispatch({
          type: types.PARENT_HIERARCHY_FAILURE,
          error,
        }),
      );
  };
}

/**
 * Populates organization.organizationInfo with the organizationInfo of the passed organizationId.
 * @returns {Function}
 */
export const getOrganizationInfo = (organizationId) => {
  return (dispatch, getState, {organization}) => {
    dispatch({type: types.GET_ORGANIZATION_REQUEST});
    const locale = get(getState(), 'localization.locale');

    return organization
      .getOrganization(organizationId, locale)
      .then((result) => {
        const fullPermissions = get(getState(), 'session.fullPermissions');
        const hasRootAccess = !!fullPermissions[DEFAULT_PARENT_ID];
        const permissionsMap = get(getState(), 'session.permissions');
        const organizationInfo = {
          ...result.data,
          isClient: result.data?.parentId === DEFAULT_PARENT_ID,
          parentAccessAllowed:
            hasRootAccess ||
            !!(result.data?.parentId && permissionsMap.find((item) => item.orgId === result.data?.parentId)),
        };

        //get parent org if auth.
        if (organizationInfo.parentAccessAllowed) {
          organization
            .getOrganization(organizationInfo.parentId)
            .then((result) => {
              organizationInfo[DEFAULT_PARENT_KEY] = result.data;
            })
            .finally(() => {
              return dispatch({
                type: types.GET_ORGANIZATION_SUCCESS,
                payload: organizationInfo,
              });
            });
        } else {
          return dispatch({
            type: types.GET_ORGANIZATION_SUCCESS,
            payload: organizationInfo,
          });
        }
      })
      .catch((err) => {
        // Sometimes incorrect organizationId returns 422
        // Sometimes the same request will return 404
        if (err.response?.status === 422 || err.response?.status === 404) {
          // redirect user to /404 route.
          // TODO: Change this when UX decides what to do
          return dispatch({type: types.GET_ORGANIZATION_FAILURE});
        }
        dispatch({type: types.GET_ORGANIZATION_FAILURE});
      });
  };
};

export const updateClientUrlAndHotline = (url, hotline) => {
  return (dispatch, getState, {organization}) => {
    dispatch({type: types.UPDATE_CLIENT_URL_AND_HOTLINE_REQUEST});
    const state = getState();
    const orgInfo = state?.organization?.organizationInfo;
    const organizationSubscriptions = state?.organization?.organizationSubscriptions;

    const hasHotline = organizationSubscriptions?.productSolutions?.some((item) => {
      return item?.features?.find((subscription) => subscription.id === HOTLINE)?.enabled;
    });

    const toast = hasHotline
      ? {
          success: <FormattedMessage {...messages.urlAndHotlineUpdateSuccess} />,
          failure: <FormattedMessage {...messages.urlAndHotlineUpdateFailure} />,
        }
      : {
          success: <FormattedMessage {...messages.urlUpdateSuccess} />,
          failure: <FormattedMessage {...messages.urlUpdateFailure} />,
        };

    return organization
      .updateOrganization({
        ...orgInfo,
        types: orgInfo?.types?.map(({type}) => type),
        [DEFAULT_CLIENT_URL_STRING_KEY]: url,
        [DEFAULT_HOTLINE_KEY]: hotline,
      })
      .then((response) => {
        dispatch({
          type: types.UPDATE_CLIENT_URL_AND_HOTLINE_SUCCESS,
          payload: response?.data,
        });
        dispatch(notificationActions.showSuccessNotification(toast.success));
      })
      .catch((error) => {
        dispatch({type: types.UPDATE_CLIENT_URL_AND_HOTLINE_FAILURE, payload: error});
        dispatch(notificationActions.showErrorNotification(toast.failure));
        throw error;
      });
  };
};

export const updateClientUrlString = (url) => {
  return (dispatch, getState, {organization}) => {
    dispatch({type: types.UPDATE_CLIENT_URL_STRING_REQUEST});
    const orgInfo = get(getState(), 'organization.organizationInfo');
    return organization
      .updateOrganization({
        ...orgInfo,
        types: orgInfo?.types?.map(({type}) => type),
        clientUrlString: url,
      })
      .then((response) => {
        dispatch({
          type: types.UPDATE_CLIENT_URL_STRING_SUCCESS,
          payload: response?.data,
        });
        dispatch(notificationActions.showSuccessNotification(<FormattedMessage {...messages.urlUpdateSuccess} />));
      })
      .catch((error) => {
        dispatch({type: types.UPDATE_CLIENT_URL_STRING_FAILURE, payload: error});
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.urlUpdateFailure} />));
        throw error;
      });
  };
};

export const updateClientConfigHotlineNumber = (hotline) => {
  return (dispatch, getState, {organization}) => {
    dispatch({type: types.UPDATE_CLIENT_CONFIG_HOTLINE_NUMBER_REQUEST});
    const orgInfo = get(getState(), 'organization.organizationInfo');
    const orgId = orgInfo?.id;
    return organization
      .updateOrganization({
        ...orgInfo,
        // per HORIZONWEB-8620:
        // types needs to be an array of strings, not array of objects
        // ie: "types": ["DIRECT"] rather than  "types": [{"type": "DIRECT", "name": "Direct"}]
        types: orgInfo?.types?.map(({type}) => type),
        hotline,
      })
      .then((response) => {
        dispatch({
          type: types.UPDATE_CLIENT_CONFIG_HOTLINE_NUMBER_SUCCESS,
          payload: response.data,
        });
        dispatch(notificationActions.showSuccessNotification(<FormattedMessage {...messages.hotlineUpdateSuccess} />));
        dispatch(getOrganizationInfo(orgId));
        dispatch(pushToViewPage());
      })
      .catch((error) => {
        dispatch({type: types.UPDATE_CLIENT_CONFIG_HOTLINE_NUMBER_FAILURE, payload: error});
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.hotlineUpdateFailure} />));
      });
  };
};

function pushToViewPage() {
  return (dispatch, getState) => {
    const pathname = getState().router.location.pathname;
    const viewPagePath = pathname?.replace('/edit', '');
    return dispatch(push(viewPagePath));
  };
}

/**
 *
 * @param organizationId
 * @returns {function(*, *, {users: *}): Promise<unknown>}
 */
export const getOrganizationSubscriptions = (organizationId) => {
  return (dispatch, getState, {users}) => {
    if (organizationId === DEFAULT_PARENT_ID) {
      return;
    }

    const locale = get(getState(), 'localization.locale');
    dispatch({type: types.GET_ORGANIZATION_SUBSCRIPTIONS_REQUEST});

    return users
      .getOrganizationSubscriptions(organizationId, locale)
      .then((results) => {
        return dispatch({
          type: types.GET_ORGANIZATION_SUBSCRIPTIONS_SUCCESS,
          payload: results.data,
        });
      })
      .catch(() => {
        dispatch({
          type: types.GET_ORGANIZATION_SUBSCRIPTIONS_FAILURE,
          payload: {},
        });
      });
  };
};

export const updateOrganizationSubscriptions = (orgId, subscriptions, basePartPath) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.UPDATE_ORGANIZATION_SUBSCRIPTIONS_REQUEST});
    return users
      .updateOrganizationSubscriptions(orgId, subscriptions)
      .then(() => {
        dispatch({
          type: types.UPDATE_ORGANIZATION_SUBSCRIPTIONS_SUCCESS,
        });
        dispatch(push(`/${basePartPath}/${orgId}/${orgId}/${ORG_TAB_PATHS.SUBSCRIPTIONS}`));
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage {...messages.organizationSubscriptionsUpdatedSuccess} />,
          ),
        );
        dispatch(sessionActions.getCurrentUser());
      })
      .catch(() => {
        dispatch({type: types.UPDATE_ORGANIZATION_SUBSCRIPTIONS_FAILURE});
        dispatch(
          notificationActions.showErrorNotification(
            <FormattedMessage {...messages.organizationSubscriptionsUpdateError} />,
          ),
        );
      });
  };
};

export const getOrganizationActiveSeats = (organizationId) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.GET_ORGANIZATION_ACTIVE_SEATS_REQUEST});

    return users
      .getOrganizationActiveSeats(organizationId)
      .then((results) => {
        return dispatch({
          type: types.GET_ORGANIZATION_ACTIVE_SEATS_SUCCESS,
          payload: results.data,
        });
      })
      .catch(() => {
        dispatch({
          type: types.GET_ORGANIZATION_ACTIVE_SEATS_FAILURE,
          payload: [],
        });
      });
  };
};

/**
 * Updates seatCount then calls getOrganizationInfo to update redux store with new maxSeatsCount.
 * @returns {Function}
 */
export const setMaxSeats = (orgId, seatCount) => {
  return (dispatch, _getState, {organization}) => {
    dispatch({type: types.UPDATE_ORGANIZATION_MAX_SEATS_REQUEST});

    return organization
      .setOrganizationMaxSeatCount(orgId, seatCount)
      .then((results) => {
        dispatch({
          type: types.UPDATE_ORGANIZATION_MAX_SEATS_SUCCESS,
          payload: results.data,
        });
        dispatch(push(`${ROUTE_URLS.CLIENTS}/${orgId}/${orgId}/${ORG_TAB_PATHS.USERS}`));
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage {...messages.organizationMaxSeatsUpdatedSuccess} />,
          ),
        );
      })
      .catch(() => {
        dispatch({type: types.UPDATE_ORGANIZATION_MAX_SEATS_FAILURE});
      });
  };
};

export const getOrganizationRoles = (organizationId) => {
  return (dispatch, getState, {users}) => {
    const locale = get(getState(), 'localization.locale');
    dispatch({type: types.GET_ORGANIZATION_ROLES_REQUEST});

    return users
      .getOrganizationRoles(organizationId, locale)
      .then((results) => {
        dispatch({
          type: types.GET_ORGANIZATION_ROLES_SUCCESS,
          payload: results.data,
        });
      })
      .catch((error) => {
        return dispatch({
          type: types.GET_ORGANIZATION_ROLES_FAILURE,
          error,
        });
      });
  };
};
export const getOrganizationPermissionHierarchy = (organizationId) => {
  return (dispatch, getState, {users}) => {
    const locale = get(getState(), 'localization.locale');
    dispatch({type: types.GET_ORGANIZATION_PERMISSIONS_HIERARCHY_REQUEST});

    return Promise.all([users.getProductSolutions(locale), users.getOrganizationSubscriptions(organizationId, locale)])
      .then(([allSolutionResults, orgSolutionResults]) => {
        const permissionTree = assignPermissionsToOrgSolutions(allSolutionResults, orgSolutionResults);

        const {permissionHierarchy} = parseSolutions({data: permissionTree});

        dispatch({
          type: types.GET_ORGANIZATION_PERMISSIONS_HIERARCHY_SUCCESS,
          payload: permissionHierarchy,
        });
      })
      .catch((error) => {
        return dispatch({
          type: types.GET_ORGANIZATION_PERMISSIONS_HIERARCHY_FAILURE,
          error,
        });
      });
  };
};

export const createCustomRole = (organizationId, customRoleBody, basePartPath) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.CREATE_CUSTOM_ROLE_REQUEST});

    const payload = formatRolePayload(customRoleBody);

    return users
      .createCustomRole(organizationId, payload)
      .then(() => {
        dispatch({
          type: types.CREATE_CUSTOM_ROLE_SUCCESS,
        });
        dispatch(push(`/${basePartPath}/${organizationId}/${organizationId}/${ORG_TAB_PATHS.ROLES}`));
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage {...messages.organizationCustomRoleCreatedDescription} />,
            'generic-success',
            <FormattedMessage {...messages.organizationCustomRoleCreatedTitle} />,
          ),
        );
      })
      .catch(() => {
        dispatch({type: types.CREATE_CUSTOM_ROLE_FAILURE});
      });
  };
};

export const getOrganizationRoleUserCount = (organizationId, roleDefinitionId) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.GET_ORGANIZATION_ROLE_USER_COUNT_REQUEST});

    return users
      .getOrganizationUserCountForRole(organizationId, roleDefinitionId)
      .then((results) => {
        return dispatch({
          type: types.GET_ORGANIZATION_ROLE_USER_COUNT_SUCCESS,
          payload: results.data,
        });
      })
      .catch(() => {
        dispatch({
          type: types.GET_ORGANIZATION_ROLE_USER_COUNT_FAILURE,
          payload: [],
        });
      });
  };
};

export const getOrganizationRoleDetail = (organizationId, organizationRoleId) => {
  return (dispatch, getState, {users}) => {
    const locale = get(getState(), 'localization.locale');
    dispatch({type: types.GET_ORGANIZATION_ROLE_DETAIL_REQUEST});

    return Promise.all([
      users.getProductSolutions(locale),
      users.getOrganizationRoleDetail(organizationId, organizationRoleId, locale),
      users.getOrganizationSubscriptions(organizationId, locale),
    ])
      .then(([allSolutionResults, orgRolesResults, orgSolutionResults]) => {
        const permissionTree = assignPermissionsToOrgSolutions(allSolutionResults, orgSolutionResults);
        const {permissionHierarchy} = parseSolutions({data: permissionTree});
        const roleDetail = orgRolesResults.data;

        dispatch({
          type: types.GET_ORGANIZATION_ROLE_DETAIL_SUCCESS,
          payload: {permissionHierarchy, roleDetail},
        });
      })
      .catch((error) => {
        return dispatch({
          type: types.GET_ORGANIZATION_ROLE_DETAIL_FAILURE,
          error,
        });
      });
  };
};
/**
 *
 * @param organizationId {string}
 * @param roleDefinitionIdToRemove {string} - the roleDefinition.id of the current role that we want to reassign
 * @param roleDefinitionIdToAdd {string} - the roleDefinition.id of the new role to which we are reassigning
 * @returns {function(*, *, {users: *}): Promise<unknown>}
 */
export const bulkReassign = (organizationId, roleDefinitionIdToRemove, roleDefinitionIdToAdd) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.BULK_REASSIGN_REQUEST});

    return users
      .putBulkReassign(organizationId, roleDefinitionIdToRemove, roleDefinitionIdToAdd)
      .then((results) => {
        dispatch({
          type: types.BULK_REASSIGN_SUCCESS,
        });
        //ensure the count of users is updated on the OrganizationRolePage 'users' tab WEBTM-2343 after a bulk re-assign
        dispatch(getOrganizationRoleUserCount(organizationId, roleDefinitionIdToRemove));

        const numberOfUsersReAssigned = results.data.numberOfUsersReAssigned;

        return users
          .getOrganizationRoles(organizationId)
          .then((results) => results.data)
          .then((roles) => {
            const roleToRemove = roles?.filter((role) => role.roleDefinition.id === roleDefinitionIdToRemove)[0];
            const roleToAdd = roles?.filter((role) => role.roleDefinition.id === roleDefinitionIdToAdd)[0];

            const roleNameToRemove = roleToRemove?.roleDefinition.name;
            const roleNameToAdd = roleToAdd?.roleDefinition.name;
            _showBulkReassignSuccessNotification(numberOfUsersReAssigned, roleNameToRemove, roleNameToAdd);
          })
          .catch(console.error);
      })
      .catch((error) => {
        dispatch({
          type: types.BULK_REASSIGN_FAILURE,
          error,
        });
        _showBulkReassignErrorNotification();
      });

    function _showBulkReassignSuccessNotification(numberOfUsersReAssigned, roleNameToRemove, roleNameToAdd) {
      dispatch(
        notificationActions.showSuccessNotification(
          <FormattedMessage
            {...messages.bulkReassignSuccessDescription}
            values={{numberOfUsersReAssigned, roleNameToRemove, roleNameToAdd}}
          />,
          'generic-key',
          <FormattedMessage {...messages.bulkReassignSuccess} />,
        ),
      );
    }

    function _showBulkReassignErrorNotification() {
      dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.bulkReassignFailure} />));
    }
  };
};

export const updateCustomRole = (organizationId, roleDefinitionId, customRoleBody, orgRoleId, basePartPath) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.UPDATE_CUSTOM_ROLE_REQUEST});

    const payload = formatRolePayload(customRoleBody);
    const redirectPath = `/${basePartPath}/${organizationId}/${organizationId}/${ORG_TAB_PATHS.ROLES}/${orgRoleId}/${ORG_ROLE_TAB_PATHS.ROLE_DETAILS}`;

    return users
      .updateOrganizationCustomRole(organizationId, roleDefinitionId, payload)
      .then(() => {
        dispatch({
          type: types.UPDATE_CUSTOM_ROLE_SUCCESS,
        });
        dispatch(push(redirectPath));
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage {...messages.organizationCustomRoleUpdatedTitle} />,
          ),
        );
      })
      .catch(() => {
        dispatch({type: types.UPDATE_CUSTOM_ROLE_FAILURE});
      });
  };
};

export const deleteCustomRole = (organizationId, roleDefinitionId, redirectUrl) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.DELETE_CUSTOM_ROLE_REQUEST});

    return users
      .deleteOrganizationCustomRole(organizationId, roleDefinitionId)
      .then(() => {
        dispatch({
          type: types.DELETE_CUSTOM_ROLE_SUCCESS,
        });
        dispatch(push(redirectUrl));
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage {...messages.organizationCustomRoleDeletedTitle} />,
          ),
        );
        dispatch(getOrganizationRoles(organizationId));
      })
      .catch(() => {
        dispatch(
          notificationActions.showErrorNotification(
            <FormattedMessage {...messages.organizationCustomRoleDeletedFail} />,
          ),
        );
        dispatch({type: types.DELETE_CUSTOM_ROLE_FAILURE});
      });
  };
};

export const bulkAssign = (organization, roleDefinition) => {
  return (dispatch, _getState, {users}) => {
    dispatch({type: types.BULK_ASSIGN_REQUEST});

    return users
      .putBulkAssign(organization.id, roleDefinition.id)
      .then((_results) => {
        dispatch({
          type: types.BULK_ASSIGN_SUCCESS,
        });
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage
              {...messages.bulkAssignSuccessDescription}
              values={{orgName: organization.name, roleName: roleDefinition.name}}
            />,
            'generic-key',
            <FormattedMessage {...messages.bulkAssignSuccess} />,
          ),
        );
      })
      .catch((error) => {
        dispatch({
          type: types.BULK_ASSIGN_FAILURE,
          error,
        });
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.bulkAssignFailure} />));
      });
  };
};
